import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Rating,
  CardActionArea,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Agent } from '../types/Agent';

interface AgentCardProps {
  agent: Agent;
}

const AgentCard: React.FC<AgentCardProps> = ({ agent }) => {
  const navigate = useNavigate();
  
  // Function to get the image URL or base64
  const getImageUrl = () => {
    // Si c'est l'agent DeepSite, utiliser le logo spécifique
    if (agent.name === "DeepSite") {
      return "/deepsite_logo.png";
    }

    // Si l'agent a une image stockée en base64
    if (agent.image && agent.image.startsWith('data:image')) {
      return agent.image;
    }

    // Si l'image est stockée comme une chaîne base64 dans la base de données
    if (agent.image && agent.image.startsWith('Binary.createFromBase64')) {
      const base64String = agent.image.match(/\('(.+)'\)/);
      if (base64String && base64String[1]) {
        return `data:image/png;base64,${base64String[1]}`;
      }
    }

    // Utiliser le chemin de l'image directement depuis les données de l'agent
    return agent.image;
  };
  
  const handleCardClick = () => {
    if (agent.name === "DeepSite") {
      // Rediriger vers la page DeepSite
      navigate('/deepsite');
    } else if (agent.name === "Qwen2.5-Coder") {
      // Rediriger vers la page Qwen 2.5
      navigate('/qwen2.5');
    } else if (agent.name.toLowerCase().includes("easycontrol") || agent.name.toLowerCase().includes("ghibli")) {
      // Rediriger vers la page EasyControl Ghibli
      navigate('/easycontrol-ghibli');
    } else if (agent.name.toLowerCase().includes("flux") || agent.name.toLowerCase().includes("flux.1")) {
      // Rediriger vers la page FLUX.1
      navigate('/flux-1');
    } else if (agent.name.toLowerCase().includes("sf3d") || agent.name.toLowerCase().includes("stable fast 3d")) {
      // Rediriger vers la page SF3D
      navigate('/sf3d');
    } else {
      // Redirection par défaut
      navigate(`/agent/${agent._id}`);
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
        minHeight: '400px',
      }}
    >
      <CardActionArea onClick={handleCardClick} sx={{ height: '100%' }}>
        <CardMedia
          component="img"
          height="200"
          image={getImageUrl()}
          alt={agent.name}
          sx={{ objectFit: 'cover' }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {agent.name}
            </Typography>
            <Rating value={agent.rating || 0} readOnly size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {agent.description}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {agent.category?.map((cat: string, index: number) => (
              <Chip 
                key={index} 
                label={cat} 
                size="small"
                sx={{ 
                  backgroundColor: '#f0f0f0',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              />
            ))}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AgentCard;